import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Tags, ChevronLeft, ChevronRight, FileText, Trash2, Upload } from "lucide-react";
import { DocumentsProvider, useDocuments } from "./modal/DocumentsContext";
import CreateDocumentModal from "./ui/CreateDocumentModal";
import ImportDocxModal from "./ui/ImportDocxModal";
import CategoryManagerModal from "./ui/CategoryManagerModal";

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function DocumentsContent() {
  const {
    isAdmin, documents, totalCount, page, totalPages, loading, error,
    search, setSearch, categoryFilter, setCategoryFilter, categories,
    setPage, deleteDocument,
  } = useDocuments();
  const navigate = useNavigate();
  const basePath = isAdmin ? "/admin/documents" : "/employee/documents";

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) setSearch(searchInput); }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search, setSearch]);

  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full bg-surface border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
            placeholder="Search documents by title…"
          />
        </div>

        <select
          value={categoryFilter ?? ""}
          onChange={(e) => setCategoryFilter(e.target.value === "" ? null : Number(e.target.value))}
          className="bg-surface border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <span className="text-xs text-muted-foreground font-inter">
          {totalCount} document{totalCount === 1 ? "" : "s"}
        </span>

        {isAdmin && (
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setShowCategories(true)}
              className="flex items-center gap-2 px-3 py-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground text-sm font-medium rounded-xl font-inter transition-colors"
            >
              <Tags className="w-4 h-4" />
              Manage Categories
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-2 px-3 py-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground text-sm font-medium rounded-xl font-inter transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import .docx
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-xl font-inter transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Document
            </button>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-400 font-inter bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground font-inter text-sm">
            No documents yet{search || categoryFilter ? " matching these filters" : ""}.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {documents.map((d) => {
              const category = categories.find((c) => c.id === d.category_id);
              return (
                <div
                  key={d.id}
                  className="relative group bg-surface border border-border rounded-2xl hover:border-primary/60 transition-colors font-inter"
                >
                  <button
                    onClick={() => navigate(`${basePath}/${d.id}`)}
                    className="block w-full text-left p-4"
                  >
                    <div className="flex items-center gap-2 mb-1.5 pr-6">
                      <FileText className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-sm font-semibold text-foreground truncate">{d.title}</span>
                    </div>
                    {d.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{d.description}</p>
                    )}
                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                      {category && (
                        <span className="text-[11px] bg-primary/15 text-primary rounded-lg px-2 py-0.5">{category.name}</span>
                      )}
                      {d.tags.map((t) => (
                        <span key={t} className="text-[11px] bg-surface-muted border border-border/60 text-muted-foreground rounded-lg px-2 py-0.5">{t}</span>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Updated {formatDate(d.updated_at)}</p>
                  </button>
                  {isAdmin && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Delete "${d.title}"? This removes it and its content — it can't be undone from here.`)) return;
                        try {
                          await deleteDocument(d.id);
                        } catch (err: any) {
                          window.alert(err?.response?.data?.detail || err?.message || "Failed to delete document");
                        }
                      }}
                      title="Delete document"
                      className="absolute top-3 right-3 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm font-inter">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-muted-foreground">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {showCreate && (
        <CreateDocumentModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); navigate(`${basePath}/${id}`); }}
        />
      )}
      {showImport && (
        <ImportDocxModal
          onClose={() => setShowImport(false)}
          onImported={(id) => { setShowImport(false); navigate(`${basePath}/${id}`); }}
        />
      )}
      {showCategories && <CategoryManagerModal onClose={() => setShowCategories(false)} />}
    </div>
  );
}

export default function DocumentsPage() {
  return (
    <DocumentsProvider>
      <div className="flex flex-col gap-6 h-full">
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">Documents</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">
            A mini Google Docs inside HRMS — rich-text documents you tag, search, share, and version, without leaving the app.
          </p>
        </div>
        <DocumentsContent />
      </div>
    </DocumentsProvider>
  );
}
